import * as annotations from './mutations/annotations';
import * as calls from './mutations/calls';
import * as channels from './mutations/channels';
import * as checklists from './mutations/checklists';
import * as comments from './mutations/comments';
import * as inboxProfile from './mutations/inbox-profile';
import * as knowledge from './mutations/knowledge';
import * as messages from './mutations/messages';
import * as people from './mutations/people';
import * as storage from './mutations/storage';
import * as storageFolders from './mutations/storage-folders';
import * as taskRelations from './mutations/task-relations';
import * as tasks from './mutations/tasks';
import * as textureLinks from './mutations/texture-links';
import * as thumbnails from './mutations/thumbnails';
import * as tree from './mutations/tree';
import * as userGroups from './mutations/user-groups';
import * as workspace from './mutations/workspace';
import { undoLast } from './undo';

export type MutationMode = 'replicated' | 'local' | 'mockOnly';

type MutationRun = (...args: never[]) => unknown;

interface MutationSpec {
  run: MutationRun;
  mode: MutationMode;
}

function replicated<F extends MutationRun>(run: F): { run: F; mode: 'replicated' } {
  return { run, mode: 'replicated' };
}

function local<F extends MutationRun>(run: F): { run: F; mode: 'local' } {
  return { run, mode: 'local' };
}

function mockOnly<F extends MutationRun>(run: F): { run: F; mode: 'mockOnly' } {
  return { run, mode: 'mockOnly' };
}

export const MUTATION_TABLE = {
  createTask: replicated(tasks.createTask),
  setTaskTitle: replicated(tasks.setTaskTitle),
  setTaskDescription: replicated(tasks.setTaskDescription),
  setStatus: replicated(tasks.setStatus),
  moveTask: replicated(tasks.moveTask),
  setAssignee: replicated(tasks.setAssignee),
  setPriority: replicated(tasks.setPriority),
  setEstimate: replicated(tasks.setEstimate),
  setTaskDates: replicated(tasks.setTaskDates),
  setMilestone: replicated(tasks.setMilestone),
  setLabels: replicated(tasks.setLabels),
  setTaskType: replicated(tasks.setTaskType),
  softDeleteTask: replicated(tasks.softDeleteTask),
  createChecklistItem: replicated(checklists.createChecklistItem),
  toggleChecklistItem: replicated(checklists.toggleChecklistItem),
  renameChecklistItem: replicated(checklists.renameChecklistItem),
  deleteChecklistItem: replicated(checklists.deleteChecklistItem),
  moveChecklistItem: replicated(checklists.moveChecklistItem),
  setChecklistAssignee: replicated(checklists.setChecklistAssignee),
  addComment: replicated(comments.addComment),
  editComment: replicated(comments.editComment),
  resolveComment: replicated(comments.resolveComment),
  unresolveComment: replicated(comments.unresolveComment),
  setCommentAssignee: replicated(comments.setCommentAssignee),
  addAttachment: replicated(comments.addAttachment),
  markAttachmentReady: mockOnly(comments.markAttachmentReady),
  removeAttachment: replicated(comments.removeAttachment),
  discardDraftAttachments: replicated(comments.discardDraftAttachments),
  updateTaskStatusDef: replicated(workspace.updateTaskStatusDef),
  updateTaskType: replicated(workspace.updateTaskType),
  setProjectStatuses: replicated(workspace.setProjectStatuses),
  setProjectTypes: replicated(workspace.setProjectTypes),
  updateProject: replicated(workspace.updateProject),
  updateFolder: replicated(workspace.updateFolder),
  deleteFolder: replicated(workspace.deleteFolder),
  updateMilestone: replicated(workspace.updateMilestone),
  createLabel: replicated(workspace.createLabel),
  updateLabel: replicated(workspace.updateLabel),
  deleteLabel: replicated(workspace.deleteLabel),
  createTaskType: replicated(workspace.createTaskType),
  deleteTaskType: replicated(workspace.deleteTaskType),
  createFolder: replicated(tree.createFolder),
  moveFolder: replicated(tree.moveFolder),
  createProject: replicated(tree.createProject),
  moveProject: replicated(tree.moveProject),
  archiveProject: replicated(tree.archiveProject),
  createMilestone: replicated(tree.createMilestone),
  moveMilestone: replicated(tree.moveMilestone),
  archiveMilestone: replicated(tree.archiveMilestone),
  moveChannelFolder: replicated(tree.moveChannelFolder),
  setActiveWorkspace: local(workspace.setActiveWorkspace),
  setSubscription: replicated(taskRelations.setSubscription),
  linkTasks: replicated(taskRelations.linkTasks),
  unlinkTasks: replicated(taskRelations.unlinkTasks),
  createTimeEntry: replicated(taskRelations.createTimeEntry),
  deleteTimeEntry: replicated(taskRelations.deleteTimeEntry),
  sendMessage: replicated(messages.sendMessage),
  replyInThread: replicated(messages.replyInThread),
  editMessage: replicated(messages.editMessage),
  softDeleteMessage: replicated(messages.softDeleteMessage),
  toggleReaction: replicated(messages.toggleReaction),
  togglePin: replicated(messages.togglePin),
  markRead: replicated(messages.markRead),
  markUnread: replicated(messages.markUnread),
  toggleSavedMessage: replicated(messages.toggleSavedMessage),
  setMessageDraft: local(messages.setMessageDraft),
  startCall: replicated(calls.startCall),
  joinCall: replicated(calls.joinCall),
  leaveCall: replicated(calls.leaveCall),
  setCallDevice: replicated(calls.setCallDevice),
  createChannelFolder: replicated(channels.createChannelFolder),
  deleteChannelFolder: replicated(channels.deleteChannelFolder),
  updateChannelFolder: replicated(channels.updateChannelFolder),
  moveChannelToFolder: replicated(channels.moveChannelToFolder),
  createChannel: replicated(channels.createChannel),
  openDirect: replicated(channels.openDirect),
  joinChannel: replicated(channels.joinChannel),
  leaveChannel: replicated(channels.leaveChannel),
  inviteToChannel: replicated(channels.inviteToChannel),
  kickFromChannel: replicated(channels.kickFromChannel),
  setChannelTopic: replicated(channels.setChannelTopic),
  setMuted: replicated(channels.setMuted),
  archiveChannel: replicated(channels.archiveChannel),
  inboxMarkRead: replicated(inboxProfile.inboxMarkRead),
  inboxMarkUnread: replicated(inboxProfile.inboxMarkUnread),
  inboxArchive: replicated(inboxProfile.inboxArchive),
  inboxDelete: replicated(inboxProfile.inboxDelete),
  inboxSnooze: replicated(inboxProfile.inboxSnooze),
  inboxRestore: replicated(inboxProfile.inboxRestore),
  updateProfile: replicated(inboxProfile.updateProfile),
  setPresence: replicated(inboxProfile.setPresence),
  setTyping: mockOnly(inboxProfile.setTyping),
  setFilter: local(inboxProfile.setFilter),
  resetFilters: local(inboxProfile.resetFilters),
  undoLast: mockOnly(undoLast),
  createUser: replicated(people.createUser),
  createAgent: replicated(people.createAgent),
  setUserActive: replicated(people.setUserActive),
  setUserRole: replicated(people.setUserRole),
  createDoc: replicated(knowledge.createDoc),
  renameDoc: replicated(knowledge.renameDoc),
  setDocBody: replicated(knowledge.setDocBody),
  moveDoc: replicated(knowledge.moveDoc),
  setDocOwner: replicated(knowledge.setDocOwner),
  setDocEditLocked: replicated(knowledge.setDocEditLocked),
  setDocSharing: replicated(knowledge.setDocSharing),
  setDocCover: replicated(knowledge.setDocCover),
  archiveDoc: replicated(knowledge.archiveDoc),
  restoreDoc: replicated(knowledge.restoreDoc),
  deleteDoc: replicated(knowledge.deleteDoc),
  updateStorageSettings: replicated(storage.updateStorageSettings),
  createStorageFolder: replicated(storageFolders.createStorageFolder),
  renameStorageFolder: replicated(storageFolders.renameStorageFolder),
  moveStorageFolder: replicated(storageFolders.moveStorageFolder),
  removeStorageFolder: replicated(storageFolders.removeStorageFolder),
  renameAttachment: replicated(storageFolders.renameAttachment),
  moveAttachmentToFolder: replicated(storageFolders.moveAttachmentToFolder),
  createUserGroup: replicated(userGroups.createUserGroup),
  renameUserGroup: replicated(userGroups.renameUserGroup),
  moveUserGroup: replicated(userGroups.moveUserGroup),
  setUserGroupAppearance: replicated(userGroups.setUserGroupAppearance),
  removeUserGroup: replicated(userGroups.removeUserGroup),
  addUserToGroup: replicated(userGroups.addUserToGroup),
  removeUserFromGroup: replicated(userGroups.removeUserFromGroup),
  createAnnotation: replicated(annotations.createAnnotation),
  deleteAnnotation: replicated(annotations.deleteAnnotation),
  resolveAnnotation: replicated(annotations.resolveAnnotation),
  reopenAnnotation: replicated(annotations.reopenAnnotation),
  createModelAnnotation: replicated(annotations.createModelAnnotation),
  addAnnotationComment: replicated(annotations.addAnnotationComment),
  deleteAnnotationComment: replicated(annotations.deleteAnnotationComment),
  deleteModelAnnotation: replicated(annotations.deleteModelAnnotation),
  setAttachmentThumbnail: mockOnly(thumbnails.setAttachmentThumbnail),
  setTextureLink: replicated(textureLinks.setTextureLink),
  clearTextureLink: replicated(textureLinks.clearTextureLink),
} as const;

export const MUTATIONS: Readonly<Record<string, MutationSpec>> = MUTATION_TABLE;

export type ReplicatedMutationName = {
  [K in keyof typeof MUTATION_TABLE]: (typeof MUTATION_TABLE)[K]['mode'] extends 'replicated' ? K : never;
}[keyof typeof MUTATION_TABLE];
